// QM Handbuch wizard state. Mirrors the legacy `web/public/app.js` shape so
// existing localStorage entries keep working. The wizard is 4 steps:
//   1. Document uploads + website + logo
//   2. Big company-data form (sections A–G, repeatable persons & references)
//   3. Editable preview with autosave
//   4. Download

export interface QmPerson {
  name_funktion: string;
  qualifikation: string;
  erfahrung: string;
  sprachen: string;
}

export interface QmReferenz {
  ziel: string;
  branche: string;
  zeitraum: string;
  rolle: string;
  ergebnis: string;
}

export interface QmForm {
  // Step 1 – uploads
  websiteUrl: string;
  onboardingDoc: string;
  cvs: string;
  companyProfile: string;
  certificates: string;
  miscDocs: string;
  // Section A – Stammdaten
  unternehmensname: string;
  sitzAdresse: string;
  gruendung: string;
  mitarbeitende: string;
  geschaeftsfuehrung: string;
  kurzprofil: string;
  // Section B – Beratungsfelder
  kernfelder: string[];
  umsetzung: string[];
  nichtAngeboten: string;
  // Section C – Zielgruppen
  zielgruppen: string[];
  branchenfokus: string;
  anlaesse: string[];
  // Section D – Team
  personen: QmPerson[];
  referenzen: QmReferenz[];
  // Section E – Personal & Infrastruktur
  personalgewinnung: string;
  weiterbildung: string;
  kapazitaeten: string;
  infrastrukturTools: string;
  netzwerkePartner: string;
  // Section F – Beratungsprozess
  erstkontakt: string;
  bedarfsermittlung: string;
  auftragsklarung: string;
  durchfuehrung: string;
  dokumentation: string;
  feedbackNachbetreuung: string;
  // Section G – Qualitätsmanagement
  qualitaetsziele: string;
  interneKontrollen: string;
  beschwerdenAbweichungen: string;
  kvpLessonsLearned: string;
  risikenChancen: string;
  interneUeberpruefung: string;
}

/**
 * "import" = the wizard is on the new "Daten importieren" step (rendered in
 * front of the existing input/preview/download steps). The numeric `step`
 * still tracks position inside the original 4-step flow once the user has
 * either submitted an import or skipped past it.
 */
export type QmPhase = "import" | "wizard";

export interface QmState {
  phase: QmPhase;
  step: 1 | 2 | 3 | 4;
  maxReachedStep: 1 | 2 | 3 | 4;
  form: QmForm;
  currentSlug: string | null;
}

export const EMPTY_QM_FORM: QmForm = {
  websiteUrl: "",
  onboardingDoc: "",
  cvs: "",
  companyProfile: "",
  certificates: "",
  miscDocs: "",
  unternehmensname: "",
  sitzAdresse: "",
  gruendung: "",
  mitarbeitende: "",
  geschaeftsfuehrung: "",
  kurzprofil: "",
  kernfelder: [],
  umsetzung: [],
  nichtAngeboten: "",
  zielgruppen: [],
  branchenfokus: "",
  anlaesse: [],
  personen: [],
  referenzen: [],
  personalgewinnung: "",
  weiterbildung: "",
  kapazitaeten: "",
  infrastrukturTools: "",
  netzwerkePartner: "",
  erstkontakt: "",
  bedarfsermittlung: "",
  auftragsklarung: "",
  durchfuehrung: "",
  dokumentation: "",
  feedbackNachbetreuung: "",
  qualitaetsziele: "",
  interneKontrollen: "",
  beschwerdenAbweichungen: "",
  kvpLessonsLearned: "",
  risikenChancen: "",
  interneUeberpruefung: "",
};

export const INITIAL_QM_STATE: QmState = {
  phase: "import",
  step: 1,
  maxReachedStep: 1,
  form: EMPTY_QM_FORM,
  currentSlug: null,
};

const STORAGE_KEY = "zww-agent.qm-wizard-state";

export function loadPersistedQmState(): QmState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL_QM_STATE;
    const parsed = JSON.parse(raw) as Partial<QmState>;
    // Pre-FOR-475 saves had no `phase` field; users mid-wizard had already
    // moved past import implicitly, so default to "wizard" instead of
    // dragging them back to the new import step on reload.
    const phase: QmPhase = parsed.phase === "import" ? "import" : "wizard";
    return {
      ...INITIAL_QM_STATE,
      ...parsed,
      phase,
      form: { ...EMPTY_QM_FORM, ...(parsed.form ?? {}) },
    };
  } catch {
    return INITIAL_QM_STATE;
  }
}

export function persistQmState(state: QmState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore — wizard still works in-memory
  }
}

export function clearPersistedQmState(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Match the legacy `buildQmSlug` exactly so existing run dirs stay reachable. */
export function buildQmSlug(name: string): string {
  const ts = new Date().toISOString().replace(/[^0-9]/g, "").slice(0, 14);
  const slugPart = (name || "handbuch")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40);
  return `qm_${slugPart}_${ts}`;
}
